import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../_shared';

export { RATE_MODE_OPTIONS };

export const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'OTHER', label: 'Otro' },
] as const;

export const SINGLE_PREMIUM_MIN_UF = 3000;
export const DEATH_CAPITAL_FLOOR_UF = 150;
export const DEATH_CAPITAL_MAX_UNDER_66 = 20000;
export const DEATH_CAPITAL_MAX_FROM_66 = 2500;
export const DEATH_CAPITAL_MIN_RATIO = 0.015;
export const RECOMMENDED_RATE_UNTIL_2027 = 0.021;
export const RECOMMENDED_RATE_FROM_2028 = 0.015;
export const MAX_PROJECTION_RATE = 0.05;

export const lifeMaxSchema = z.object({
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  initial_contribution: z.coerce.number().nonnegative(),
  death_insured_capital: z.coerce.number().min(DEATH_CAPITAL_FLOOR_UF, `Mínimo ${DEATH_CAPITAL_FLOOR_UF} UF`),
  ...rateModeFields,
  unscheduled_annual_contribution: z.coerce.number().nonnegative(),
  // Optional client-elected extra top-up. UNIQUE, in UF, lands on
  // `direct_contribution_month` (1-based). Separate from the legacy
  // `initial_contribution` above.
  direct_contribution_amount: z.coerce.number().nonnegative(),
  direct_contribution_month: z.coerce.number().int().min(1, 'Mínimo 1 (inicio)'),
})
  .superRefine(refineRateMode)
  .superRefine((values, ctx) => {
    // The 5% ceiling only applies to the fixed rate; stepped tiers are validated
    // by the shared schedule schema.
    if (
      values.rate_mode === 'fixed'
      && values.projection_rate !== undefined
      && values.projection_rate > MAX_PROJECTION_RATE
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Máximo ${MAX_PROJECTION_RATE * 100}% anual`,
        path: ['projection_rate'],
      });
    }
  })
  .superRefine((values, ctx) => {
    // Mirrors the backend's entry-payment floor under the new engine.
    if (values.initial_contribution < SINGLE_PREMIUM_MIN_UF) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Aporte inicial debe ser mínimo ${SINGLE_PREMIUM_MIN_UF} UF`,
        path: ['initial_contribution'],
      });
    }
  });

export type LifeMaxFormValues = z.infer<typeof lifeMaxSchema>;

export const isFixedRate = (v: LifeMaxFormValues) => v.rate_mode === 'fixed';
export const isSteppedRate = (v: LifeMaxFormValues) => v.rate_mode === 'stepped';

export const lifeMaxDefaults: LifeMaxFormValues = {
  date_of_birth: '1990-01-01',
  gender: 'MALE',
  initial_contribution: SINGLE_PREMIUM_MIN_UF,
  death_insured_capital: DEATH_CAPITAL_FLOOR_UF,
  ...rateModeDefaults,
  projection_rate: RECOMMENDED_RATE_UNTIL_2027,
  unscheduled_annual_contribution: 0,
  direct_contribution_amount: 0,
  direct_contribution_month: 1,
};
